import { useState, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Users, Plus, Edit2, Trash2, Phone, List, Check, Merge, X, Layers } from 'lucide-react';

export default function ContactsTab({ contacts, onAdd, onEdit, onDelete, onAddToRoute, onUpdateContact, onMergeContacts }) {
  const [bulkEdit, setBulkEdit] = useState(false);
  const [editedPhones, setEditedPhones] = useState({});
  const [saving, setSaving] = useState({});
  const [mergeMode, setMergeMode] = useState(null); // name string when active
  const [mergeSelection, setMergeSelection] = useState(new Set());
  const [mergeAllMode, setMergeAllMode] = useState(false);
  const [mergeAllSelection, setMergeAllSelection] = useState(new Set()); // ids selected across all groups

  const formatPhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/^\+44\s*/, '0');
  };

  const startBulkEdit = () => {
    const phones = {};
    contacts.forEach(c => { phones[c.id] = c.phone || ''; });
    setEditedPhones(phones);
    setBulkEdit(true);
  };

  const saveSingle = useCallback(async (contact) => {
    const newPhone = (editedPhones[contact.id] || '').trim();
    if (newPhone === (contact.phone || '')) return;
    setSaving(prev => ({ ...prev, [contact.id]: true }));
    try {
      await onUpdateContact({ ...contact, phone: newPhone });
      toast.success(`${contact.name} updated`);
    } catch (e) {
      toast.error(`Failed to update ${contact.name}`);
    }
    setSaving(prev => ({ ...prev, [contact.id]: false }));
  }, [editedPhones, onUpdateContact]);

  const saveAll = useCallback(async () => {
    let count = 0;
    for (const contact of contacts) {
      const newPhone = (editedPhones[contact.id] || '').trim();
      if (newPhone !== (contact.phone || '')) {
        try { await onUpdateContact({ ...contact, phone: newPhone }); count++; } catch (e) {}
      }
    }
    if (count > 0) toast.success(`Updated ${count} contact${count > 1 ? 's' : ''}`);
    setBulkEdit(false);
  }, [contacts, editedPhones, onUpdateContact]);

  const startMerge = (name) => {
    const dupes = contacts.filter(c => c.name.toLowerCase() === name.toLowerCase());
    setMergeMode(name);
    // Pre-select all same-name contacts
    setMergeSelection(new Set(dupes.map(c => c.id)));
  };

  const toggleMergeSelect = (id) => {
    setMergeSelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const confirmMerge = () => {
    if (mergeSelection.size < 2) { toast.error('Select at least 2 contacts to merge'); return; }
    const ids = [...mergeSelection];
    onMergeContacts(ids);
    setMergeMode(null);
    setMergeSelection(new Set());
  };

  // Group duplicate-name contacts
  const duplicateGroups = useMemo(() => {
    const map = {};
    contacts.forEach(c => {
      const key = c.name.trim().toLowerCase();
      if (!key) return;
      if (!map[key]) map[key] = { name: c.name, items: [] };
      map[key].items.push(c);
    });
    return Object.values(map).filter(g => g.items.length > 1);
  }, [contacts]);

  const startMergeAll = () => {
    if (duplicateGroups.length === 0) { toast.error('No duplicates to merge'); return; }
    // Pre-select every duplicate contact
    const ids = new Set();
    duplicateGroups.forEach(g => g.items.forEach(c => ids.add(c.id)));
    setMergeAllSelection(ids);
    setMergeAllMode(true);
  };

  const toggleMergeAllSelect = (id) => {
    setMergeAllSelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const confirmMergeAll = async () => {
    // For each group, collect selected ids and merge if >=2
    let totalGroups = 0;
    for (const group of duplicateGroups) {
      const selectedIds = group.items.map(c => c.id).filter(id => mergeAllSelection.has(id));
      if (selectedIds.length >= 2) {
        await onMergeContacts(selectedIds);
        totalGroups++;
      }
    }
    if (totalGroups === 0) toast.error('Select at least 2 contacts in any group');
    else toast.success(`Merged ${totalGroups} group${totalGroups > 1 ? 's' : ''}`);
    setMergeAllMode(false);
    setMergeAllSelection(new Set());
  };

  if (contacts.length === 0) {
    return (
      <>
        <div className="p-3 border-b border-[rgba(0,0,0,0.08)]">
          <Button onClick={onAdd} className="w-full h-12 text-base font-semibold bg-[#007aff] hover:bg-[#0066d6] rounded-xl transition-all duration-200" data-testid="add-contact-btn">
            <Plus className="w-5 h-5 mr-2" />Add Contact
          </Button>
        </div>
        <div className="sidebar-scroll">
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Users className="w-12 h-12 text-[#86868b] mb-4" />
            <h3 className="text-lg font-semibold text-[#1d1d1f] mb-2">No contacts</h3>
            <p className="text-[#86868b] text-sm">Save frequently used addresses as contacts</p>
          </div>
        </div>
      </>
    );
  }

  const sorted = [...contacts].sort((a, b) => a.name.localeCompare(b.name));

  const duplicateNames = (() => {
    const counts = {};
    contacts.forEach(c => { const n = c.name.toLowerCase(); counts[n] = (counts[n] || 0) + 1; });
    return new Set(Object.keys(counts).filter(n => counts[n] > 1));
  })();

  return (
    <>
      <div className="p-3 border-b border-[rgba(0,0,0,0.08)] space-y-2">
        {mergeAllMode ? (
          <>
            <div className="text-sm font-medium text-[#1d1d1f]">Review duplicate groups to merge</div>
            <div className="flex gap-2">
              <Button onClick={confirmMergeAll} className="flex-1 h-10 text-sm font-semibold bg-[#ff9500] hover:bg-[#e68a00] rounded-xl" data-testid="confirm-merge-all-btn">
                <Layers className="w-4 h-4 mr-2" />Merge All Selected
              </Button>
              <Button onClick={() => { setMergeAllMode(false); setMergeAllSelection(new Set()); }} variant="outline" className="h-10 px-4 rounded-xl">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </>
        ) : mergeMode ? (
          <>
            <div className="text-sm font-medium text-[#1d1d1f]">Select contacts to merge with "{mergeMode}"</div>
            <div className="flex gap-2">
              <Button onClick={confirmMerge} disabled={mergeSelection.size < 2} className="flex-1 h-10 text-sm font-semibold bg-[#ff9500] hover:bg-[#e68a00] rounded-xl">
                <Merge className="w-4 h-4 mr-2" />Merge ({mergeSelection.size})
              </Button>
              <Button onClick={() => { setMergeMode(null); setMergeSelection(new Set()); }} variant="outline" className="h-10 px-4 rounded-xl">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button onClick={onAdd} className="w-full h-12 text-base font-semibold bg-[#007aff] hover:bg-[#0066d6] rounded-xl transition-all duration-200" data-testid="add-contact-btn">
              <Plus className="w-5 h-5 mr-2" />Add Contact
            </Button>
            {bulkEdit ? (
              <Button onClick={saveAll} variant="outline" className="w-full h-10 text-sm font-semibold border-[#34c759] text-[#34c759] hover:bg-[#34c759]/10 rounded-xl" data-testid="bulk-save-btn">
                <Check className="w-4 h-4 mr-2" />Save All & Exit
              </Button>
            ) : (
              <Button onClick={startBulkEdit} variant="outline" className="w-full h-10 text-sm font-semibold rounded-xl" data-testid="bulk-edit-btn">
                <List className="w-4 h-4 mr-2" />Bulk Edit
              </Button>
            )}
            {duplicateGroups.length > 0 && !bulkEdit && (
              <Button onClick={startMergeAll} variant="outline" className="w-full h-10 text-sm font-semibold border-[#ff9500] text-[#ff9500] hover:bg-[#ff9500]/10 rounded-xl" data-testid="merge-all-btn">
                <Layers className="w-4 h-4 mr-2" />Merge All Duplicates ({duplicateGroups.length})
              </Button>
            )}
          </>
        )}
      </div>
      <div className="sidebar-scroll">
        {mergeAllMode ? (
          <div className="p-2 space-y-4">
            {duplicateGroups.map((group) => (
              <div key={group.name} className="space-y-1">
                <div className="text-xs font-semibold text-[#86868b] uppercase tracking-wide px-2 pt-1">{group.name} ({group.items.length})</div>
                {group.items.map((contact) => {
                  const postcodes = contact.postcodes || [contact.postcode];
                  const isSelected = mergeAllSelection.has(contact.id);
                  return (
                    <div key={contact.id}
                      className={`destination-card cursor-pointer transition-all duration-200 ${isSelected ? 'ring-2 ring-[#ff9500] bg-[#ff9500]/5' : ''}`}
                      onClick={() => toggleMergeAllSelect(contact.id)}
                      data-testid={`merge-all-contact-${contact.id}`}
                    >
                      <div className="flex-1 py-2 px-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-[#ff9500] border-[#ff9500]' : 'border-[#c7c7cc]'}`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold">{contact.name}</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {postcodes.map((pc, idx) => (
                                <span key={idx} className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{pc}</span>
                              ))}
                            </div>
                            {contact.phone && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                                <Phone className="w-3 h-3" />{formatPhone(contact.phone)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-2 space-y-2">
          {sorted.map((contact) => {
            const postcodes = contact.postcodes || [contact.postcode];
            const isInMergeMode = !!mergeMode;
            const isMergeCandidate = mergeMode && contact.name.toLowerCase() === mergeMode.toLowerCase();
            const isSelected = mergeSelection.has(contact.id);

            // In merge mode, only show same-name contacts
            if (isInMergeMode && !isMergeCandidate) return null;

            return (
              <div key={contact.id}
                className={`destination-card transition-all duration-200 ${isInMergeMode ? 'cursor-pointer' : ''} ${isSelected ? 'ring-2 ring-[#ff9500] bg-[#ff9500]/5' : ''}`}
                onClick={isMergeCandidate ? () => toggleMergeSelect(contact.id) : undefined}
                data-testid={`contact-${contact.id}`}
              >
                <div className="flex-1 py-2 px-3">
                  {bulkEdit && !isInMergeMode ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-bold text-[#1d1d1f]">{contact.name}</div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {postcodes.map((pc, idx) => (
                              <span key={idx} className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{pc}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-[#86868b] flex-shrink-0" />
                        <Input type="tel" placeholder="Enter phone number..." value={editedPhones[contact.id] || ''} onChange={(e) => setEditedPhones(prev => ({ ...prev, [contact.id]: e.target.value }))} onBlur={() => saveSingle(contact)} className="h-10 text-sm font-mono" data-testid={`bulk-phone-${contact.id}`} />
                        {saving[contact.id] && <div className="w-4 h-4 border-2 border-[#007aff] border-t-transparent rounded-full animate-spin flex-shrink-0" />}
                      </div>
                    </div>
                  ) : isMergeCandidate ? (
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-[#ff9500] border-[#ff9500]' : 'border-[#c7c7cc]'}`}>
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold">{contact.name}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {postcodes.map((pc, idx) => (
                            <span key={idx} className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{pc}</span>
                          ))}
                        </div>
                        {contact.phone && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <Phone className="w-3 h-3" />{formatPhone(contact.phone)}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold">{contact.name}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {postcodes.map((pc, idx) => (
                            <span key={idx} className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{pc}</span>
                          ))}
                        </div>
                        {contact.phone && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <Phone className="w-3 h-3" />{formatPhone(contact.phone)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {duplicateNames.has(contact.name.toLowerCase()) && (
                          <Button variant="ghost" size="sm" onClick={() => startMerge(contact.name)} className="p-2 text-[#ff9500]" title="Merge same-name contacts"><Merge className="w-4 h-4" /></Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => onAddToRoute(contact)} className="p-2 text-primary" title="Add to route"><Plus className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => onEdit(contact)} className="p-2" title="Edit"><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => onDelete(contact.id)} className="p-2 hover:text-destructive" title="Delete"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </>
  );
}
